import React from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { useScope } from '../context/PermissionContext';
import LabModuleSettings from '../components/settings/LabModuleSettings';
import AdmissionModuleSettings from '../components/settings/AdmissionModuleSettings';
import ProcedurePaymentSettings from '../components/settings/ProcedurePaymentSettings';
import HipSettings from '../components/settings/HipSettings';
import ScanShareCountersSettings from '../components/settings/ScanShareCountersSettings';
import HiuSettings from '../components/settings/HiuSettings';
import useAbdmEnabled from '../hooks/useAbdmEnabled';
import AdminPageHeader from '../components/common/AdminPageHeader';
import './SettingsPage.css';

/**
 * Settings — admin-only landing page for per-org module toggles.
 *
 * Reachable from the avatar dropdown's "Settings" item (UserProfilePanel),
 * which is itself admin-gated via the same isAdmin check. Houses the module
 * toggles that used to be stacked inside Fees & Billing: Laboratory module,
 * Admission (IPD) module, and the "Procedure autocompletion" auto-complete
 * toggle (ProcedurePaymentSettings) — none of these are fee/billing concerns,
 * they're per-org feature flags. Also houses HipSettings and HiuSettings —
 * the org's ABDM HIP ID and HIU ID, each a single DB-backed value used by
 * both directions of its own live ABDM resolver (outbound publish/request +
 * inbound webhook routing). HIP = this org publishing its own records (M2).
 * HIU = this org requesting another facility's records (M3). Two distinct
 * NHA-issued identities, not interchangeable.
 */
export default function SettingsPage() {
  const { isDark } = useTheme();
  const { isAdmin } = useScope();
  const { show: abdmOn } = useAbdmEnabled();

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  // Permission gate (after all hooks)
  if (!isAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="sp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="sp-main">
        <div className="sp-title-row">
          <h1 className="sp-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
            ⚙ Settings
          </h1>
          <p className="sp-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
            Per-organisation module toggles
          </p>
        </div>
        <div className="sp-body">
          <LabModuleSettings />
          <AdmissionModuleSettings />
          <ProcedurePaymentSettings />
          <HipSettings />
          {abdmOn && (
            <>
              <ScanShareCountersSettings />
              <HiuSettings />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
